import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { ReactNode, useEffect, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { sharedColors, sharedStyles } from '../../utils/Style';
import { FileInput } from '../FileInput/FileInput';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { EnhanceImageDialog } from './EnhanceImageDialog';

interface ImageInputProps {
  title?: string;
  existingImage?: string;
  onClickGenerate?: () => void;
  onUpload?: (file?: File) => void;
  onDelete?: () => void;
  bottomSection?: ReactNode;
  enableEnhancement?: boolean;
}

export const ImageInput = (props: ImageInputProps) => {
  const [file, setFile] = useState<File | undefined>(undefined);
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [clickedDelete, setClickedDelete] = useState(false);
  const [showEnhanceDialog, setShowEnhanceDialog] = useState(false);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setImageUrl(url);

      return () => {
        URL.revokeObjectURL(url);
        setImageUrl(undefined);
      };
    }

    return () => {};
  }, [file]);

  useEffect(() => {
    setFile(undefined);
  }, [props.existingImage]);

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 400,
        height: 'fit-content',
        px: 3,
        pt: 1,
        pb: 3,
        gap: 1.5,
      }}
    >
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili medyayı silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={() => {
            setClickedDelete(false);
            props.onDelete?.();
          }}
          color='error'
        />
      )}

      <EnhanceImageDialog
        image={showEnhanceDialog ? file : undefined}
        onClose={() => setShowEnhanceDialog(false)}
        onEnhance={setFile}
      />

      {props.title && (
        <>
          <Typography variant='h6'>{props.title}</Typography>
          <Divider sx={{ mt: -1 }} />
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          flexGrow: 1,
          alignItems: 'end',
        }}
      >
        <Paper
          sx={{ backgroundColor: sharedColors.gray2, width: 120, height: 120 }}
        >
          {imageUrl || props.existingImage ? (
            <img
              src={imageUrl || props.existingImage}
              alt={props.title ?? 'Mevcut'}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ImageIcon fontSize='large' />
            </Box>
          )}
        </Paper>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            gap: 0.5,
            height: '100%',
          }}
        >
          {file?.name && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <Tooltip title={file.name}>
                <Box
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    flexGrow: 1,
                    maxWidth: 250,
                  }}
                >
                  {file?.name}
                </Box>
              </Tooltip>
              <IconButton
                onClick={() => {
                  setFile(undefined);
                  setImageUrl(undefined);
                }}
                color='error'
                size='small'
                sx={{ minHeight: 0, p: 0 }}
              >
                <CancelIcon />
              </IconButton>
            </Box>
          )}

          {!!file && props.enableEnhancement && (
            <Button
              variant='outlined'
              color='primary'
              startIcon={<AutoAwesomeIcon />}
              onClick={() => setShowEnhanceDialog(true)}
              sx={{ ...sharedStyles.buttonText, width: '100%' }}
            >
              İyileştir
            </Button>
          )}

          {!!props.onClickGenerate && (
            <Button
              variant='outlined'
              color='primary'
              startIcon={<AutoAwesomeIcon />}
              onClick={() => props.onClickGenerate?.()}
              sx={{ ...sharedStyles.buttonText, width: '100%' }}
            >
              Oluştur
            </Button>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1.5,
              justifyContent: 'space-between',
            }}
          >
            <Button
              disabled={!props.onDelete || !props.existingImage}
              onClick={() => setClickedDelete(true)}
              color='error'
              variant='contained'
              size='small'
              startIcon={<DeleteIcon />}
              sx={{ ...sharedStyles.buttonText }}
            >
              Sil
            </Button>
            <FileInput
              disabled={!props.onUpload}
              file={file}
              onChange={setFile}
            />
            <Button
              disabled={!props.onUpload || !file}
              onClick={() => props.onUpload?.(file)}
              variant='contained'
              size='small'
              startIcon={<SaveIcon />}
              sx={{ ...sharedStyles.buttonText }}
            >
              Kaydet
            </Button>
          </Box>
        </Box>
      </Box>

      {props.bottomSection}
    </Paper>
  );
};
